/**
 * The unauthenticated password-reset calls. They bypass `apiService` on
 * purpose: its 401 handling would bounce a signed-out reader to `/signin`.
 * Sign in itself goes through `useAuth().login`.
 */

export class AuthRequestError extends Error {
  kind: 'network' | 'validation' | 'server';
  constructor(kind: AuthRequestError['kind'], message: string) {
    super(message);
    this.kind = kind;
  }
}

const base = () => process.env.REACT_APP_API_BASE_URL ?? '';

async function postJson(path: string, body: unknown): Promise<Response> {
  try {
    return await fetch(`${base()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new AuthRequestError('network', 'Failed to fetch');
  }
}

/**
 * POST /api/auth/password/forgot/ {email}. Enumeration-safe: any HTTP answer
 * counts as sent, so the page can't reveal whether the account exists. Only
 * a network failure throws.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await postJson('/api/auth/password/forgot/', { email });
}

/**
 * POST /api/auth/password/reset/validate/ {uid, token} → {valid}. The API
 * doesn't say why a link is invalid (expired, used, unknown), so neither do
 * we. A 429 or 5xx is a failed check, not an invalid link — it throws so the
 * page can offer a retry instead of sending the reader for a new email.
 */
export async function validateResetLink(
  uid: string | null,
  token: string | null,
): Promise<boolean> {
  if (!uid || !token) return false;
  const res = await postJson('/api/auth/password/reset/validate/', {
    uid,
    token,
  });
  if (res.status === 429 || res.status >= 500) {
    throw new AuthRequestError('server', `HTTP ${res.status}`);
  }
  if (!res.ok) return false;
  const data = (await res.json().catch(() => ({}))) as { valid?: boolean };
  return data.valid === true;
}

type ResetErrorBody = Partial<
  Record<
    | 'non_field_errors'
    | 'new_password'
    | 'confirm_password'
    | 'detail'
    | 'message'
    | 'error',
    string | string[]
  >
>;

const sentence = (v: string | string[]) => {
  const s = Array.isArray(v) ? v.join(' ') : v;
  return /[.!?]$/.test(s.trim()) ? s.trim() : `${s.trim()}.`;
};

/**
 * POST /api/auth/password/reset/ {uid, token, new_password,
 * confirm_password}. A 400 carries Django's validator messages; they are
 * returned verbatim as a `validation` error.
 */
export async function resetPassword(
  uid: string,
  token: string,
  password: string,
): Promise<void> {
  const res = await postJson('/api/auth/password/reset/', {
    uid,
    token,
    new_password: password,
    confirm_password: password,
  });
  if (res.ok) return;
  const data = (await res.json().catch(() => ({}))) as ResetErrorBody;
  const message =
    data.non_field_errors ??
    data.new_password ??
    data.confirm_password ??
    data.detail ??
    data.message ??
    data.error;
  if (message && message.length)
    throw new AuthRequestError('validation', sentence(message));
  throw new AuthRequestError('server', `HTTP ${res.status}`);
}
